<template>
  <div :class="['month-filter', isDarkTheme ? 'theme-dark' : 'theme-light']">
    <div class="month-filter-copy">
      <p class="month-filter-eyebrow">{{ label }}</p>
      <strong>{{ displayPeriod }}</strong>
    </div>

    <div class="month-filter-controls">
      <select :value="selectedMonth" class="filter-select" @change="handleMonthChange">
        <option value="">Todos os meses</option>
        <option v-for="month in months" :key="month.value" :value="month.value">
          {{ month.label }}
        </option>
      </select>

      <select :value="selectedYear" class="filter-select filter-select-year" @change="handleYearChange">
        <option value="">Todos os anos</option>
        <option v-for="year in yearOptions" :key="year" :value="String(year)">
          {{ year }}
        </option>
      </select>

      <button
        v-if="modelValue"
        type="button"
        class="clear-btn"
        @click="$emit('update:modelValue', '')"
      >
        Limpar
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useStore } from 'vuex'

type MonthOption = {
  value: string
  label: string
}

export default defineComponent({
  name: 'MonthYearFilter',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Filtro do dashboard'
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const store = useStore()
    const months: MonthOption[] = [
      { value: '01', label: 'Janeiro' },
      { value: '02', label: 'Fevereiro' },
      { value: '03', label: 'Março' },
      { value: '04', label: 'Abril' },
      { value: '05', label: 'Maio' },
      { value: '06', label: 'Junho' },
      { value: '07', label: 'Julho' },
      { value: '08', label: 'Agosto' },
      { value: '09', label: 'Setembro' },
      { value: '10', label: 'Outubro' },
      { value: '11', label: 'Novembro' },
      { value: '12', label: 'Dezembro' }
    ]

    const selectedYear = computed(() => props.modelValue.split('-')[0] || '')
    const selectedMonth = computed(() => props.modelValue.split('-')[1] || '')
    const isDarkTheme = computed(() => store.state.isDarkTheme)

    const displayPeriod = computed(() => {
      if (!selectedYear.value && !selectedMonth.value) {
        return 'Período Completo'
      }

      if (selectedYear.value && !selectedMonth.value) {
        return `Ano de ${selectedYear.value}`
      }

      const monthLabel = months.find((month) => month.value === selectedMonth.value)?.label

      if (selectedYear.value && monthLabel) {
        return `${monthLabel} de ${selectedYear.value}`
      }

      return 'Periodo personalizado'
    })

    const fixedYear = '2026'
    const yearOptions = [2026]

    const emitValue = (year: string, month: string) => {
      if (year && month) {
        emit('update:modelValue', `${year}-${month}`)
        return
      }

      if (year) {
        emit('update:modelValue', `${year}`)
        return
      }

      emit('update:modelValue', '')
    }

    const handleMonthChange = (event: Event) => {
      const target = event.target as HTMLSelectElement
      emitValue(selectedYear.value || fixedYear, target.value)
    }

    const handleYearChange = (event: Event) => {
      const target = event.target as HTMLSelectElement
      emitValue(target.value, selectedMonth.value)
    }

    return {
      months,
      selectedMonth,
      selectedYear,
      isDarkTheme,
      displayPeriod,
      yearOptions,
      handleMonthChange,
      handleYearChange
    }
  }
})
</script>

<style scoped>
.month-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--month-filter-bg);
  border: 1px solid var(--month-filter-border);
  box-shadow: var(--month-filter-shadow);
}

.month-filter.theme-light {
  --month-filter-bg:
    linear-gradient(135deg, rgba(244, 247, 255, 0.98), rgba(233, 239, 251, 0.96)),
    var(--bg-secondary);
  --month-filter-border: color-mix(in srgb, rgba(73, 121, 203, 0.18) 68%, var(--border-color));
  --month-filter-shadow: 0 10px 24px rgba(34, 56, 92, 0.08);
  --month-filter-title: #203a61;
  --month-filter-eyebrow: #6c82a1;
  --month-filter-select-border: color-mix(in srgb, rgba(73, 121, 203, 0.18) 68%, var(--border-color));
  --month-filter-select-bg: rgba(255, 255, 255, 0.95);
  --month-filter-select-color: #243854;
  --month-filter-clear-border: color-mix(in srgb, rgba(73, 121, 203, 0.18) 68%, var(--border-color));
  --month-filter-clear-bg: rgba(255, 255, 255, 0.72);
  --month-filter-clear-color: #5e7393;
  --month-filter-clear-hover-color: #243854;
  --month-filter-clear-hover-bg: rgba(255, 255, 255, 0.94);
}

.month-filter.theme-dark {
  --month-filter-bg:
    linear-gradient(135deg, rgba(20, 32, 57, 0.9), rgba(11, 20, 39, 0.84)),
    var(--bg-secondary);
  --month-filter-border: color-mix(in srgb, var(--border-color) 80%, transparent);
  --month-filter-shadow: 0 10px 24px rgba(0, 0, 0, 0.16);
  --month-filter-title: #f3f7ff;
  --month-filter-eyebrow: #b9c8df;
  --month-filter-select-border: color-mix(in srgb, var(--border-color) 82%, transparent);
  --month-filter-select-bg: color-mix(in srgb, var(--bg-primary) 80%, transparent);
  --month-filter-select-color: var(--text-primary);
  --month-filter-clear-border: color-mix(in srgb, var(--border-color) 82%, transparent);
  --month-filter-clear-bg: transparent;
  --month-filter-clear-color: var(--text-secondary);
  --month-filter-clear-hover-color: var(--text-primary);
  --month-filter-clear-hover-bg: color-mix(in srgb, var(--bg-primary) 72%, transparent);
}

.month-filter-copy {
  display: grid;
  gap: 4px;
}

.month-filter-copy strong {
  color: var(--month-filter-title);
  font-size: 18px;
  text-transform: capitalize;
}

.month-filter-eyebrow {
  color: var(--month-filter-eyebrow);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.month-filter-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.filter-select {
  min-width: 180px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--month-filter-select-border);
  background: var(--month-filter-select-bg);
  color: var(--month-filter-select-color);
}

.filter-select-year {
  min-width: 140px;
}

.clear-btn {
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--month-filter-clear-border);
  background: var(--month-filter-clear-bg);
  color: var(--month-filter-clear-color);
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  color: var(--month-filter-clear-hover-color);
  background: var(--month-filter-clear-hover-bg);
}

@media (max-width: 640px) {
  .month-filter {
    align-items: stretch;
    flex-direction: column;
  }

  .month-filter-controls {
    width: 100%;
    justify-content: stretch;
  }

  .filter-select,
  .filter-select-year,
  .clear-btn {
    width: 100%;
  }
}
</style>
